// The University of Texas at Austin: public college page (#2596), official
// facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - UT Austin's Common Data Set 2025-2026 (file UT_PUB_CDS_2025-2026_AY_public),
//    linked as "2025-26" from Institutional Reporting's PDF CDS page. The
//    file sits on utexas.box.com, so SOURCES links the utexas.edu page that
//    publishes it. C1 applicants/admits/enrollees for Fall 2025 with the
//    residency table, C8 test policy, G1/G5 2026-2027 costs. Some headings in
//    the PDF's text layer are garbled; the counts are intact and sum.
//  - admissions.utexas.edu: Freshman (Key Freshman Admission Dates and
//    application requirements, dated to Fall 2027 by its January 15, 2027
//    and February 15, 2027 decision dates) and Review and Decision Process.
//  - College Scorecard API, school id 228778 (net price by family income).
// Texas One Stop's 2026-27 cost of attendance page gives tuition only as a
// range by program ($10,858-$13,576 resident, $42,554-$51,106 non-resident),
// so the itemized single figures come from the CDS; its figures fall inside
// those ranges.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UT Austin Common Data Set 2025-2026 (Institutional Reporting)',
        url: 'https://reports.utexas.edu/common-data-set/pdf',
        read: '2026-09-30',
    },
    freshman: {
        label: 'UT Austin Admissions, Freshman',
        url: 'https://admissions.utexas.edu/apply/freshman/',
        read: '2026-09-30',
    },
    review: {
        label: 'UT Austin Admissions, Review and Decision Process',
        url: 'https://admissions.utexas.edu/apply/review-decision-process/',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?228778-The-University-of-Texas-at-Austin',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-texas-at-austin',
    kbId: 'university_of_texas_at_austin',
    name: 'The University of Texas at Austin',
    shortName: 'UT Austin',
    aliases: ['UT', 'Texas'],
    location: 'Austin, TX',
    control: 'Public',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (UT brand colors); decoration only.
    branding: {
        primary: '#BF5700',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_texas_at_austin_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 90690,
        admitted: 20154,
        enrolled: 9900,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 46879, admitted: 16828 },
            { group: 'Out-of-state', applied: 35268, admitted: 2653 },
            { group: 'International', applied: 8543, admitted: 673 },
        ],
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UT Austin\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major. UT reviews each application for admission and for the majors you choose, and counts the competitiveness of your major, but it does not publish a rate by major. Texas students in the top 5% of their class are admitted automatically for summer and fall 2027.',
        source: 'review',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular deadline', value: 'December 1' },
        rows: [
            { plan: 'Early Action', deadline: 'October 15', decision: 'By January 15, a decision or a deferral', binding: false },
            { plan: 'Regular', deadline: 'December 1', decision: 'By February 15', binding: false },
        ],
        note: 'Supplemental materials are due October 22 for Early Action and December 10 for the regular deadline. Deferred Early Action applicants get a decision by February 15. The application opens August 1, through the Common App or ApplyTexas.',
        source: 'freshman',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'UT Austin requires at least one official SAT or ACT score, sent directly by the testing agency and received by your deadline. Official CLT scores also meet the requirement. The SAT Essay, ACT Writing and the ACT Science section are not required.',
        source: 'freshman',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [11656, 44816] },
            { item: 'Food and housing (on campus)', amounts: [15420, 15420] },
            { item: 'Books and supplies', amounts: [724, 724] },
            { item: 'Transportation', amounts: [1840, 1840] },
            { item: 'Other expenses', amounts: [3666, 3666] },
        ],
        totals: [33306, 66466],
        note: 'The Common Data Set lists no separate required fees, and it says tuition varies by program, so yours may differ from this typical figure.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 18554,
            byIncome: [
                { income: '$0 to $30,000', amount: 8979 },
                { income: '$30,001 to $48,000', amount: 9324 },
                { income: '$48,001 to $75,000', amount: 12412 },
                { income: '$75,001 to $110,000', amount: 21357 },
                { income: 'Over $110,000', amount: 30480 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UT Austin\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
